import styled from 'styled-components';

import type { Color } from '@genially/design-system';
import { BasicParagraph, SmallSubtitle, style } from '@genially/design-system';
import type { GraphicsScale } from '@genially/design-system/build/style/types/theme';

import {
  QuestionTitleWrapper,
  TotalParticipantsBigSubtitle,
} from '../../../../SidebarSummary.styled';

interface FullWidthEnabledProps {
  $isFullWidthEnabled?: boolean;
}

export const HeaderWrapper = styled.div<FullWidthEnabledProps>(
  ({ theme: { size }, $isFullWidthEnabled }) => ({
    paddingTop: $isFullWidthEnabled ? 0 : size.units(3),
    paddingBottom: $isFullWidthEnabled ? size.units(2) : 0,
    display: 'flex',
    flexDirection: 'column',
    gap: size.units(2.5),
  }),
);

export const Wrapper = styled.div<FullWidthEnabledProps>(
  ({ theme: { size }, $isFullWidthEnabled }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    width: $isFullWidthEnabled ? '100%' : undefined,
    minHeight: 0,

    [QuestionTitleWrapper]: {
      marginBottom: size.units(1.5),
    },

    [TotalParticipantsBigSubtitle]: {
      marginBottom: size.units(2),
    },
  }),
);

export const DataWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.size.units(2.5),
  width: '100%',
  maxWidth: theme.size.units(107),
  height: '100%',
  overflow: 'auto',
  padding: `0 ${theme.size.units(1)}`,

  ...style.mixins.scrollbar(theme),
}));

interface ScrollWrapperProps {
  $hasScrollTop: boolean;
  $hasScrollBottom: boolean;
}

export const ScrollWrapper = styled.div<ScrollWrapperProps>(
  ({ theme: { size, color, transition }, $hasScrollBottom, $hasScrollTop }) => ({
    position: 'relative',
    width: '100%',
    minHeight: 0,

    '::after, ::before': {
      position: 'absolute',
      content: '""',
      left: 0,
      width: '100%',
      height: size.units(9),
      pointerEvents: 'none',
      transition: transition.standard('opacity'),
      background: `linear-gradient(0deg, rgba(255, 255, 255, 0), ${color.background.reversed.default()})`,
    },

    '::before': {
      top: -1,
      opacity: $hasScrollTop ? 1 : 0,
    },

    '::after': {
      bottom: -1,
      transform: 'rotate(180deg)',
      opacity: $hasScrollBottom ? 1 : 0,
    },
  }),
);

interface DataRowWrapperProps {
  $isSelected: boolean;
}

export const DataRowWrapper = styled.div<DataRowWrapperProps>(
  ({ theme: { size, color }, $isSelected }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: size.units(1),
    width: '100%',
    backgroundColor: $isSelected
      ? color.background.secondary.default(0.1)
      : color.background.primary.disabledContrast(0.05),
    padding: `${size.units(1.5)} ${size.units(2)}`,
    borderRadius: size.units(1),
    border: $isSelected ? `1px solid ${color.border.reversed.default()}` : undefined,
  }),
);

export const DataRowTextWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minWidth: size.units(11.875),
}));

export const SurveyPercentageLabelWrapper = styled.div(
  ({ theme: { borderRadius, color, size } }) => ({
    padding: `0 ${size.units(0.5)}`,
    backgroundColor: color.background.reversed.default(),
    borderRadius: borderRadius.small,
  }),
);

export const SurveyPercentageLabel = styled(SmallSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
  fontWeight: 700,
}));

const getBackgroundColor = (color: Color, index: number) => {
  return color.background.graphics(((index % 10) + 1) as unknown as keyof GraphicsScale);
};

interface SurveyPercentageProps {
  $index: number;
  $percentage: number;
}

export const SurveyPercentage = styled.div<SurveyPercentageProps>(
  ({ theme: { color, transition, size }, $index, $percentage }) => ({
    height: size.units(4),
    minHeight: size.units(4),
    borderRadius: size.units(1),

    width: '100%',
    position: 'relative',
    backgroundColor: color.background.primary.default(0.3),

    ':before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: `${$percentage}%`,
      height: '100%',
      backgroundColor: getBackgroundColor(color, $index),
      transition: transition.standard('width'),
      borderRadius: size.units(1),
    },

    [SurveyPercentageLabelWrapper]: {
      position: 'absolute',
      right: size.units(0.5),
      top: '50%',
      transform: 'translateY(-50%)',
    },
  }),
);

export const StyledBasicParagraph = styled(BasicParagraph)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
  textAlign: 'left',
  overflow: 'hidden',
  display: '-webkit-box',
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': '3',
}));
